import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  HomeIcon,
  UsersIcon,
  ShieldCheckIcon,
  CubeIcon,
  FolderIcon,
  // CubeTransparentIcon,
  CogIcon,
  TagIcon,
  LinkIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ShoppingCartIcon,
  FireIcon,
  ClockIcon,
  TruckIcon,
  BeakerIcon,
  TableCellsIcon,
  ReceiptPercentIcon,
  DocumentChartBarIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  BoltIcon,
  UserIcon,
  ClipboardDocumentListIcon,
  GlobeAltIcon,
  MegaphoneIcon,
  ShieldExclamationIcon,
  MapPinIcon,
  NoSymbolIcon,
  GiftIcon,
} from '@heroicons/react/24/outline'
import { useApolloClient } from '@apollo/client'
import { auth } from '../lib/auth'
import { navigation } from '../lib/navigation'
import { usePermissions } from '../hooks/usePermissions'
import { useUnreadAnnouncements } from '../hooks/useUnreadAnnouncements'
import { useTerminal } from '../contexts/TerminalContext'
import { PERMISSIONS } from '../lib/permissions'
import TerminalSelectionModal from './TerminalSelectionModal'
import IncomingOrdersBanner from './IncomingOrdersBanner'
import IncomingOrdersSheet from './IncomingOrdersSheet'
import { useNewOrderChime } from '../hooks/useNewOrderChime'
import toast from 'react-hot-toast'

interface LayoutProps {
  children: React.ReactNode
}

const sidebarNavTop = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Announcements', href: '/announcements', icon: MegaphoneIcon },
  { name: 'Cash Registry', href: '/cash-registry?fullscreen=true', icon: CreditCardIcon },
  { name: 'Orders', href: '/orders', icon: ShoppingCartIcon },
  { name: 'Delivery', href: '/orders/delivery', icon: TruckIcon },
  // Driver work screen — for the DeliveryDriver role this is the only visible item.
  { name: 'Driver', href: '/driver', icon: TruckIcon },
  { name: 'Order History', href: '/orders/history', icon: ClockIcon },
  { name: 'Kitchen Display', href: '/orders/kitchen', icon: FireIcon },
]

const reportsItems = [
  { name: 'Accounting Reports', href: '/reports/accounting', icon: CalculatorIcon },
  { name: 'Daily Reports', href: '/reports/daily', icon: CalendarDaysIcon },
  { name: 'Product Reports', href: '/reports/products', icon: ChartBarIcon },
  { name: 'Delivery Reports', href: '/reports/delivery', icon: TruckIcon },
  { name: 'Time Slots', href: '/reports/time-slots', icon: ClockIcon },
]

const productManagementItems = [
  { name: 'Products', href: '/products', icon: CubeIcon },
  { name: 'Allergens & Attributes', href: '/allergens', icon: ShieldExclamationIcon },
  { name: 'Option Groups', href: '/option-groups', icon: CogIcon },
  { name: 'Option Items', href: '/option-items', icon: TagIcon },
  { name: 'Variant Options', href: '/variant-option-groups', icon: LinkIcon },
]

const costingItems = [
  { name: 'Raw Materials', href: '/costing/raw-materials', icon: BeakerIcon },
  { name: 'Recipes', href: '/costing/recipes', icon: BeakerIcon },
  { name: 'Variant Costs', href: '/costing/variants', icon: TableCellsIcon },
  { name: 'Option Item Costs', href: '/costing/option-item-costs', icon: TagIcon },
  { name: 'Bundle Costs', href: '/costing/bundles', icon: TableCellsIcon },
]

const promotionsItems = [
  { name: 'Automatic', href: '/discounts/automatic', icon: BoltIcon },
  { name: 'Coupon Codes', href: '/discounts/coupon', icon: TagIcon },
  { name: 'Staff Discounts', href: '/discounts/staff', icon: UserIcon },
]

const productionItems = [
  { name: 'End of Shift', href: '/end-of-shift', icon: ClipboardDocumentListIcon },
  // Owner 2026-09-12: marking sold out is a management task (ShiftLead and up).
  { name: 'Availability', href: '/availability', icon: NoSymbolIcon },
]

// Curry Me Club — its own group (owner 2026-09-21: not an Admin entry). `name`
// is the permission key, `label` the display text. Members is stage 3 of the
// club; its page is a placeholder until that stage lands.
const clubItems = [
  { name: 'ClubPrograms', label: 'Programs', href: '/loyalty/programs', icon: GiftIcon },
  { name: 'ClubMembers', label: 'Members', href: '/loyalty/members', icon: UsersIcon },
]

// Admin group — management surfaces. `name` is the permission key; `label` is the
// display text (the announcements item keys on 'AdminAnnouncements' to avoid
// colliding with the baseline reader "Announcements" nav item).
const adminItems = [
  { name: 'AdminAnnouncements', label: 'Announcements', href: '/admin/announcements', icon: MegaphoneIcon },
  // 'AdminDelivery' — the key 'Delivery' is the Delivery Board's; MapPinIcon
  // for the same reason TruckIcon is already taken by that board.
  { name: 'AdminDelivery', label: 'Delivery', href: '/admin/delivery', icon: MapPinIcon },
  { name: 'Users', label: 'Users', href: '/users', icon: UsersIcon },
  { name: 'Roles', label: 'Roles', href: '/roles', icon: ShieldCheckIcon },
  { name: 'Categories', label: 'Categories', href: '/categories', icon: FolderIcon },
]

const sidebarNavBottom = [
  { name: 'External Platforms', href: '/external-platforms', icon: GlobeAltIcon },
]

const Layout = ({ children }: LayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [terminalModalOpen, setTerminalModalOpen] = useState(false)
  const [reportsOpen, setReportsOpen] = useState(false)
  const [productManagementOpen, setProductManagementOpen] = useState(false)
  const [cogsOpen, setCogsOpen] = useState(false)
  const [promotionsOpen, setPromotionsOpen] = useState(false)
  const [productionOpen, setProductionOpen] = useState(false)
  const [clubOpen, setClubOpen] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const apolloClient = useApolloClient()
  const user = auth.getUser()
  const role = auth.getRole()
  const { canSeeNavigation, hasPermission } = usePermissions()
  const { unreadCount } = useUnreadAnnouncements()
  const { selectedTerminal } = useTerminal()

  // Mount once at the layout root so the chime fires regardless of which
  // route or whether the IncomingOrdersSheet is currently open.
  useNewOrderChime()

  const isReportsActive = reportsItems.some(
    item => location.pathname.startsWith(item.href)
  )

  const isProductManagementActive = productManagementItems.some(
    item => location.pathname.startsWith(item.href)
  )

  const isCogsActive = costingItems.some(
    item => location.pathname.startsWith(item.href)
  )

  const isPromotionsActive = promotionsItems.some(
    item => location.pathname.startsWith(item.href)
  )

  const isProductionActive = productionItems.some(
    item => location.pathname.startsWith(item.href)
  )

  const isClubActive = clubItems.some(
    item => location.pathname.startsWith(item.href)
  )

  const isAdminActive = adminItems.some(
    item => location.pathname.startsWith(item.href)
  )

  useEffect(() => {
    if (isReportsActive) {
      setReportsOpen(true)
    }
  }, [isReportsActive])

  useEffect(() => {
    if (isProductManagementActive) {
      setProductManagementOpen(true)
    }
  }, [isProductManagementActive])

  useEffect(() => {
    if (isCogsActive) {
      setCogsOpen(true)
    }
  }, [isCogsActive])

  useEffect(() => {
    if (isPromotionsActive) {
      setPromotionsOpen(true)
    }
  }, [isPromotionsActive])

  useEffect(() => {
    if (isProductionActive) {
      setProductionOpen(true)
    }
  }, [isProductionActive])

  useEffect(() => {
    if (isClubActive) {
      setClubOpen(true)
    }
  }, [isClubActive])

  useEffect(() => {
    if (isAdminActive) {
      setAdminOpen(true)
    }
  }, [isAdminActive])

  const handleLogout = async () => {
    try {
      // Prevent previous user's cached queries (notably `myPermissions`) from leaking into the next session.
      await apolloClient.clearStore()
    } catch (e) {
      // Non-fatal; we still want to log out.
    }
    auth.clearAuth()
    // Trigger immediate auth-aware rerenders (App listens to this)
    window.dispatchEvent(new Event('storage'))
    toast.success('Logged out successfully')
    navigation.redirectToLogin(navigate)
  }

  return (
    <div className="min-h-screen bg-brand-seasalt">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <img src="/brand/logo.svg" alt="Curry Me POS" className="h-9 w-auto" />
              <span className="text-lg font-semibold text-secondary-900">Curry Me POS</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
                     <nav className="flex-1 space-y-1 px-2 py-4">
             {sidebarNavTop
               .filter(item => canSeeNavigation(item.name))
               .map((item) => {
                 const isActive = location.pathname === item.href
                 return (
                   <Link
                     key={item.name}
                     to={item.href}
                     className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                       isActive
                         ? 'bg-primary-100 text-primary-900'
                         : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                     }`}
                     onClick={() => setSidebarOpen(false)}
                   >
                     <item.icon className="mr-3 h-5 w-5" />
                     {item.name}
                     {item.name === 'Announcements' && unreadCount > 0 && (
                       <span className="ml-auto inline-flex items-center justify-center rounded-full bg-primary-500 px-2 py-0.5 text-xs font-semibold text-white">
                         {unreadCount}
                       </span>
                     )}
                   </Link>
                 )
               })}
             {reportsItems.some(item => canSeeNavigation(item.name)) && (
               <>
                 <button
                   onClick={() => setReportsOpen(!reportsOpen)}
                   className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                     isReportsActive
                       ? 'bg-primary-50 text-primary-900'
                       : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                   }`}
                 >
                   <DocumentChartBarIcon className="mr-3 h-5 w-5" />
                   Reports
                   <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${reportsOpen ? 'rotate-180' : ''}`} />
                 </button>
                 {reportsOpen && reportsItems
                   .filter(item => canSeeNavigation(item.name))
                   .map((item) => {
                     const isActive = location.pathname.startsWith(item.href)
                     return (
                       <Link
                         key={item.name}
                         to={item.href}
                         className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                           isActive
                             ? 'bg-primary-100 text-primary-900'
                             : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                         }`}
                         onClick={() => setSidebarOpen(false)}
                       >
                         <item.icon className="mr-3 h-5 w-5" />
                         {item.name}
                       </Link>
                     )
                   })}
               </>
             )}
             {productManagementItems.some(item => canSeeNavigation(item.name)) && (
               <>
                 <button
                   onClick={() => setProductManagementOpen(!productManagementOpen)}
                   className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                     isProductManagementActive
                       ? 'bg-primary-50 text-primary-900'
                       : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                   }`}
                 >
                   <CubeIcon className="mr-3 h-5 w-5" />
                   Product Management
                   <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${productManagementOpen ? 'rotate-180' : ''}`} />
                 </button>
                 {productManagementOpen && productManagementItems
                   .filter(item => canSeeNavigation(item.name))
                   .map((item) => {
                     const isActive = location.pathname.startsWith(item.href)
                     return (
                       <Link
                         key={item.name}
                         to={item.href}
                         className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                           isActive
                             ? 'bg-primary-100 text-primary-900'
                             : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                         }`}
                         onClick={() => setSidebarOpen(false)}
                       >
                         <item.icon className="mr-3 h-5 w-5" />
                         {item.name}
                       </Link>
                     )
                   })}
               </>
             )}
             {costingItems.some(item => canSeeNavigation(item.name)) && (
               <>
                 <button
                   onClick={() => setCogsOpen(!cogsOpen)}
                   className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                     isCogsActive
                       ? 'bg-primary-50 text-primary-900'
                       : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                   }`}
                 >
                   <BeakerIcon className="mr-3 h-5 w-5" />
                   COGS
                   <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${cogsOpen ? 'rotate-180' : ''}`} />
                 </button>
                 {cogsOpen && costingItems
                   .filter(item => canSeeNavigation(item.name))
                   .map((item) => {
                     const isActive = location.pathname.startsWith(item.href)
                     return (
                       <Link
                         key={item.name}
                         to={item.href}
                         className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                           isActive
                             ? 'bg-primary-100 text-primary-900'
                             : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                         }`}
                         onClick={() => setSidebarOpen(false)}
                       >
                         <item.icon className="mr-3 h-5 w-5" />
                         {item.name}
                       </Link>
                     )
                   })}
               </>
             )}
             {promotionsItems.some(item => canSeeNavigation(item.name)) && (
               <>
                 <button
                   onClick={() => setPromotionsOpen(!promotionsOpen)}
                   className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                     isPromotionsActive
                       ? 'bg-primary-50 text-primary-900'
                       : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                   }`}
                 >
                   <ReceiptPercentIcon className="mr-3 h-5 w-5" />
                   Promotions
                   <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${promotionsOpen ? 'rotate-180' : ''}`} />
                 </button>
                 {promotionsOpen && promotionsItems
                   .filter(item => canSeeNavigation(item.name))
                   .map((item) => {
                     const isActive = location.pathname.startsWith(item.href)
                     return (
                       <Link
                         key={item.name}
                         to={item.href}
                         className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                           isActive
                             ? 'bg-primary-100 text-primary-900'
                             : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                         }`}
                         onClick={() => setSidebarOpen(false)}
                       >
                         <item.icon className="mr-3 h-5 w-5" />
                         {item.name}
                       </Link>
                     )
                   })}
               </>
             )}
             {productionItems.some(item => canSeeNavigation(item.name)) && (
               <>
                 <button
                   onClick={() => setProductionOpen(!productionOpen)}
                   className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                     isProductionActive
                       ? 'bg-primary-50 text-primary-900'
                       : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                   }`}
                 >
                   <ClipboardDocumentListIcon className="mr-3 h-5 w-5" />
                   Management
                   <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${productionOpen ? 'rotate-180' : ''}`} />
                 </button>
                 {productionOpen && productionItems
                   .filter(item => canSeeNavigation(item.name))
                   .map((item) => {
                     const isActive = location.pathname.startsWith(item.href)
                     return (
                       <Link
                         key={item.name}
                         to={item.href}
                         className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                           isActive
                             ? 'bg-primary-100 text-primary-900'
                             : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                         }`}
                         onClick={() => setSidebarOpen(false)}
                       >
                         <item.icon className="mr-3 h-5 w-5" />
                         {item.name}
                       </Link>
                     )
                   })}
               </>
             )}
             {clubItems.some(item => canSeeNavigation(item.name)) && (
               <>
                 <button
                   onClick={() => setClubOpen(!clubOpen)}
                   className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                     isClubActive
                       ? 'bg-primary-50 text-primary-900'
                       : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                   }`}
                 >
                   <GiftIcon className="mr-3 h-5 w-5" />
                   Curry Me Club
                   <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${clubOpen ? 'rotate-180' : ''}`} />
                 </button>
                 {clubOpen && clubItems
                   .filter(item => canSeeNavigation(item.name))
                   .map((item) => {
                     const isActive = location.pathname.startsWith(item.href)
                     return (
                       <Link
                         key={item.name}
                         to={item.href}
                         className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                           isActive
                             ? 'bg-primary-100 text-primary-900'
                             : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                         }`}
                         onClick={() => setSidebarOpen(false)}
                       >
                         <item.icon className="mr-3 h-5 w-5" />
                         {item.label}
                       </Link>
                     )
                   })}
               </>
             )}
             {adminItems.some(item => canSeeNavigation(item.name)) && (
               <>
                 <button
                   onClick={() => setAdminOpen(!adminOpen)}
                   className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                     isAdminActive
                       ? 'bg-primary-50 text-primary-900'
                       : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                   }`}
                 >
                   <ShieldCheckIcon className="mr-3 h-5 w-5" />
                   Admin
                   <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${adminOpen ? 'rotate-180' : ''}`} />
                 </button>
                 {adminOpen && adminItems
                   .filter(item => canSeeNavigation(item.name))
                   .map((item) => {
                     const isActive = location.pathname.startsWith(item.href)
                     return (
                       <Link
                         key={item.name}
                         to={item.href}
                         className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                           isActive
                             ? 'bg-primary-100 text-primary-900'
                             : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                         }`}
                         onClick={() => setSidebarOpen(false)}
                       >
                         <item.icon className="mr-3 h-5 w-5" />
                         {item.label}
                       </Link>
                     )
                   })}
               </>
             )}
             {sidebarNavBottom
               .filter(item => canSeeNavigation(item.name))
               .map((item) => {
                 const isActive = location.pathname === item.href
                 return (
                   <Link
                     key={item.name}
                     to={item.href}
                     className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                       isActive
                         ? 'bg-primary-100 text-primary-900'
                         : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                     }`}
                     onClick={() => setSidebarOpen(false)}
                   >
                     <item.icon className="mr-3 h-5 w-5" />
                     {item.name}
                   </Link>
                 )
               })}
           </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${
        sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4 justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-3">
                <img src="/brand/logo.svg" alt="Curry Me POS" className="h-9 w-auto" />
                <span className="text-lg font-semibold text-secondary-900">Curry Me POS</span>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
              )}
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {sidebarNavTop
              .filter(item => canSeeNavigation(item.name))
              .map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    <item.icon className={`h-5 w-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'}`} />
                    {!sidebarCollapsed && item.name}
                    {!sidebarCollapsed && item.name === 'Announcements' && unreadCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center rounded-full bg-primary-500 px-2 py-0.5 text-xs font-semibold text-white">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                )
              })}
            {sidebarCollapsed
              ? reportsItems
                  .filter(item => canSeeNavigation(item.name))
                  .map((item) => {
                    const isActive = location.pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                          isActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={item.name}
                      >
                        <item.icon className="mx-auto h-5 w-5" />
                      </Link>
                    )
                  })
              : reportsItems.some(item => canSeeNavigation(item.name)) && (
                  <>
                    <button
                      onClick={() => setReportsOpen(!reportsOpen)}
                      className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isReportsActive
                          ? 'bg-primary-50 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <DocumentChartBarIcon className="mr-3 h-5 w-5" />
                      Reports
                      <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${reportsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {reportsOpen && reportsItems
                      .filter(item => canSeeNavigation(item.name))
                      .map((item) => {
                        const isActive = location.pathname.startsWith(item.href)
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon className="mr-3 h-5 w-5" />
                            {item.name}
                          </Link>
                        )
                      })}
                  </>
                )}
            {sidebarCollapsed
              ? productManagementItems
                  .filter(item => canSeeNavigation(item.name))
                  .map((item) => {
                    const isActive = location.pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                          isActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={item.name}
                      >
                        <item.icon className="mx-auto h-5 w-5" />
                      </Link>
                    )
                  })
              : productManagementItems.some(item => canSeeNavigation(item.name)) && (
                  <>
                    <button
                      onClick={() => setProductManagementOpen(!productManagementOpen)}
                      className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isProductManagementActive
                          ? 'bg-primary-50 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <CubeIcon className="mr-3 h-5 w-5" />
                      Product Management
                      <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${productManagementOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {productManagementOpen && productManagementItems
                      .filter(item => canSeeNavigation(item.name))
                      .map((item) => {
                        const isActive = location.pathname.startsWith(item.href)
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon className="mr-3 h-5 w-5" />
                            {item.name}
                          </Link>
                        )
                      })}
                  </>
                )}
            {sidebarCollapsed
              ? costingItems
                  .filter(item => canSeeNavigation(item.name))
                  .map((item) => {
                    const isActive = location.pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                          isActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={item.name}
                      >
                        <item.icon className="mx-auto h-5 w-5" />
                      </Link>
                    )
                  })
              : costingItems.some(item => canSeeNavigation(item.name)) && (
                  <>
                    <button
                      onClick={() => setCogsOpen(!cogsOpen)}
                      className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isCogsActive
                          ? 'bg-primary-50 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <BeakerIcon className="mr-3 h-5 w-5" />
                      COGS
                      <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${cogsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {cogsOpen && costingItems
                      .filter(item => canSeeNavigation(item.name))
                      .map((item) => {
                        const isActive = location.pathname.startsWith(item.href)
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon className="mr-3 h-5 w-5" />
                            {item.name}
                          </Link>
                        )
                      })}
                  </>
                )}
            {sidebarCollapsed
              ? promotionsItems
                  .filter(item => canSeeNavigation(item.name))
                  .map((item) => {
                    const isActive = location.pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                          isActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={item.name}
                      >
                        <item.icon className="mx-auto h-5 w-5" />
                      </Link>
                    )
                  })
              : promotionsItems.some(item => canSeeNavigation(item.name)) && (
                  <>
                    <button
                      onClick={() => setPromotionsOpen(!promotionsOpen)}
                      className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isPromotionsActive
                          ? 'bg-primary-50 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <ReceiptPercentIcon className="mr-3 h-5 w-5" />
                      Promotions
                      <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${promotionsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {promotionsOpen && promotionsItems
                      .filter(item => canSeeNavigation(item.name))
                      .map((item) => {
                        const isActive = location.pathname.startsWith(item.href)
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon className="mr-3 h-5 w-5" />
                            {item.name}
                          </Link>
                        )
                      })}
                  </>
                )}
            {sidebarCollapsed
              ? productionItems
                  .filter(item => canSeeNavigation(item.name))
                  .map((item) => {
                    const isActive = location.pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                          isActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={item.name}
                      >
                        <item.icon className="mx-auto h-5 w-5" />
                      </Link>
                    )
                  })
              : productionItems.some(item => canSeeNavigation(item.name)) && (
                  <>
                    <button
                      onClick={() => setProductionOpen(!productionOpen)}
                      className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isProductionActive
                          ? 'bg-primary-50 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <ClipboardDocumentListIcon className="mr-3 h-5 w-5" />
                      Management
                      <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${productionOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {productionOpen && productionItems
                      .filter(item => canSeeNavigation(item.name))
                      .map((item) => {
                        const isActive = location.pathname.startsWith(item.href)
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon className="mr-3 h-5 w-5" />
                            {item.name}
                          </Link>
                        )
                      })}
                  </>
                )}
            {sidebarCollapsed
              ? clubItems
                  .filter(item => canSeeNavigation(item.name))
                  .map((item) => {
                    const isActive = location.pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                          isActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={item.label}
                      >
                        <item.icon className="mx-auto h-5 w-5" />
                      </Link>
                    )
                  })
              : clubItems.some(item => canSeeNavigation(item.name)) && (
                  <>
                    <button
                      onClick={() => setClubOpen(!clubOpen)}
                      className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isClubActive
                          ? 'bg-primary-50 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <GiftIcon className="mr-3 h-5 w-5" />
                      Curry Me Club
                      <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${clubOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {clubOpen && clubItems
                      .filter(item => canSeeNavigation(item.name))
                      .map((item) => {
                        const isActive = location.pathname.startsWith(item.href)
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon className="mr-3 h-5 w-5" />
                            {item.label}
                          </Link>
                        )
                      })}
                  </>
                )}
            {sidebarCollapsed
              ? adminItems
                  .filter(item => canSeeNavigation(item.name))
                  .map((item) => {
                    const isActive = location.pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                          isActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={item.label}
                      >
                        <item.icon className="mx-auto h-5 w-5" />
                      </Link>
                    )
                  })
              : adminItems.some(item => canSeeNavigation(item.name)) && (
                  <>
                    <button
                      onClick={() => setAdminOpen(!adminOpen)}
                      className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isAdminActive
                          ? 'bg-primary-50 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <ShieldCheckIcon className="mr-3 h-5 w-5" />
                      Admin
                      <ChevronDownIcon className={`ml-auto h-4 w-4 transition-transform ${adminOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {adminOpen && adminItems
                      .filter(item => canSeeNavigation(item.name))
                      .map((item) => {
                        const isActive = location.pathname.startsWith(item.href)
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon className="mr-3 h-5 w-5" />
                            {item.label}
                          </Link>
                        )
                      })}
                  </>
                )}
            {sidebarNavBottom
              .filter(item => canSeeNavigation(item.name))
              .map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    <item.icon className={`h-5 w-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'}`} />
                    {!sidebarCollapsed && item.name}
                  </Link>
                )
              })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${
        sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
      }`}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />
                              <div className="flex items-center gap-x-4">
                  <div className="flex flex-col items-end">
                    <Link to="/profile" className="text-sm text-gray-700 hover:text-gray-900" title="My profile">
                      Welcome, {user?.name} ({role?.role})
                    </Link>
                    {hasPermission(PERMISSIONS.PAYMENT_TERMINAL_SELECT) && (
                      <button
                        onClick={() => setTerminalModalOpen(true)}
                        className="text-xs text-gray-500 hover:text-gray-700 mt-0.5"
                      >
                        Terminal: {selectedTerminal === '' ? 'None' : selectedTerminal || 'Not Set'}
                      </button>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-x-2 text-sm text-gray-700 hover:text-gray-900"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    Logout
                  </button>
                </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            {children}
          </div>
        </main>
      </div>

      {/* Terminal Selection Modal */}
      <TerminalSelectionModal
        isOpen={terminalModalOpen}
        onClose={() => setTerminalModalOpen(false)}
      />

      {/* Incoming HubRise orders awaiting external handoff */}
      <IncomingOrdersBanner />
      <IncomingOrdersSheet />
    </div>
  )
}

export default Layout
